/* TEYRA STUDIO — "Make an Offer" for ready-made websites.
 *
 * Opens an accessible dialog from any [data-offer-open] button, validates the offer and prepares a WhatsApp
 * message for the studio's configured number (window.TEYRA_CFG.whatsapp). Nothing is stored or sent by the
 * site itself: the offer only reaches TEYRA STUDIO when the visitor presses Send in WhatsApp.
 * An offer never changes the published price, never creates a discount, never reserves a site and never starts a payment.
 *
 * Trigger markup (rendered by teyra.js and sections/project-detail.liquid):
 *   <button type="button" data-offer-open data-offer-slug="…" data-offer-title="…" data-offer-price="17900 MAD"
 *           data-offer-min="12000" data-offer-enabled="true" data-offer-category="…">
 *
 * Every project carries its OWN minimum offer (block setting "minimum_offer_price"). Nothing is derived, guessed or
 * defaulted: a project without a valid minimum cannot receive offers. Accepted range = [minimum, published price].
 * The values reach this script from Liquid (trusted theme settings), never from the visitor's form.
 */
(() => {
  'use strict';
  const FALLBACK = {
    fr: {
      offer_minimum: 'Offre minimale', offer_range: 'Votre proposition sera examinée par TEYRA STUDIO. Vous pouvez également acheter ce site au prix affiché.',
      offer_err_price_below: 'L’offre minimale pour ce projet est de {minimum}. Veuillez saisir un montant égal ou supérieur à ce minimum.', offer_err_price_above: 'Votre offre ne peut pas dépasser le prix affiché. Vous pouvez acheter le site à son prix affiché.',
      offer_unavail_text: 'Les offres ne sont pas disponibles pour ce projet pour le moment. Vous pouvez acheter le site à son prix affiché ou contacter TEYRA STUDIO pour discuter de vos besoins.', offer_unavail_buy: 'Acheter ce site',
      offer_cfg_disabled: 'Note propriétaire : les offres sont désactivées pour ce projet.', offer_cfg_no_price: 'Note propriétaire : ce projet n’a pas de prix affiché, les offres ne peuvent donc pas être validées.',
      offer_cfg_no_min: 'Note propriétaire : aucun prix d’offre minimum n’est configuré pour ce projet (Réglages du thème → Make an Offer).', offer_cfg_bad_min: 'Note propriétaire : le prix d’offre minimum doit être un nombre supérieur à zéro et non supérieur au prix affiché.',
      offer_wa_minimum: 'Prix d’offre minimum' },
    ar: {
      offer_minimum: 'الحد الأدنى للعرض', offer_range: 'سيتم مراجعة عرضك من طرف TEYRA STUDIO. يمكنك أيضاً شراء الموقع بالسعر المعلن.',
      offer_err_price_below: 'الحد الأدنى للعرض لهذا المشروع هو {minimum}. يرجى إدخال مبلغ يساوي هذا الحد أو يفوقه.', offer_err_price_above: 'لا يمكن أن يتجاوز عرضك السعر المعلن. يمكنك شراء الموقع بسعره المعلن.',
      offer_unavail_text: 'العروض غير متاحة لهذا المشروع حالياً. يمكنك شراء الموقع بسعره المعلن أو التواصل مع TEYRA STUDIO لمناقشة احتياجاتك.', offer_unavail_buy: 'اشترِ هذا الموقع',
      offer_cfg_disabled: 'ملاحظة للمالك: العروض متوقفة لهذا المشروع.', offer_cfg_no_price: 'ملاحظة للمالك: لا يوجد سعر معلن لهذا المشروع، لذلك لا يمكن التحقق من العروض.',
      offer_cfg_no_min: 'ملاحظة للمالك: لم يتم ضبط أدنى سعر للعرض لهذا المشروع (إعدادات القالب ← Make an Offer).', offer_cfg_bad_min: 'ملاحظة للمالك: يجب أن يكون أدنى سعر للعرض رقماً أكبر من الصفر وغير أعلى من السعر المعلن.',
      offer_wa_minimum: 'أدنى سعر للعرض' }
  };
  const T = (k, d, v) => {
    const lang = String(document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    const fb = FALLBACK[lang] && FALLBACK[lang][k];
    if (fb && !(window.TEYRA_I18N && window.TEYRA_I18N[k])) { let s = fb; if (v) Object.keys(v).forEach(n => { s = s.split('{' + n + '}').join(v[n]); }); return s; }
    return window.teyraT ? window.teyraT(k, d, v) : String(d || '');
  };

  /* Per-project minimums come from Theme settings → "Make an Offer" (rendered by layout/theme.liquid as window.TEYRA_OFFERS):
     [["project-slug", "12000"], …]. A project that is not listed (or listed as "off") cannot receive offers. */
  const offerConfig = slug => {
    const list = Array.isArray(window.TEYRA_OFFERS) ? window.TEYRA_OFFERS : [];
    const key = String(slug || '').trim().toLowerCase();
    const hit = list.find(p => Array.isArray(p) && String(p[0]).trim().toLowerCase() === key);
    return hit ? String(hit[1] == null ? '' : hit[1]).trim() : '';
  };
  const DEFAULT_CURRENCY = 'MAD';
  const MAX_AMOUNT = 10000000;
  const MAX_MESSAGE = 500;

  /* ---------- pure helpers (also used by the test-suite) ---------- */

  // Arabic-Indic / Persian digits and separators -> ASCII
  const normaliseDigits = s => String(s || '')
    .replace(/[٠-٩]/g, c => String(c.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, c => String(c.charCodeAt(0) - 0x06F0))
    .replace(/٫/g, '.').replace(/٬/g, ',');

  /* Published price ("17900 MAD", "8 000 DH", "11000") -> {amount, currency}. Unknown price -> amount null. */
  const parsePublished = raw => {
    const m = String(raw || '').trim().match(/^(\d[\d\s  .,]*)(.*)$/);
    if (!m) return { amount: null, currency: DEFAULT_CURRENCY };
    const n = Number(m[1].replace(/[\s  .,]/g, ''));
    return { amount: Number.isFinite(n) && n > 0 ? n : null, currency: (m[2] || '').trim() || DEFAULT_CURRENCY };
  };

  /* Owner-configured minimum ("12000", "12 000", "12,000 MAD") -> {amount|null}. The minimum has no currency of its own:
     it is always expressed in the project's published currency. A different currency suffix = misconfiguration. */
  const parseMinimum = (raw, currency) => {
    const m = String(raw || '').trim().match(/^(\d[\d\s  .,]*)(.*)$/);
    if (!m) return { amount: null };
    const unit = (m[2] || '').trim();
    if (unit && String(currency || '').toLowerCase() !== unit.toLowerCase()) return { amount: null };
    const n = Number(m[1].replace(/[\s  .,]/g, ''));
    return { amount: Number.isFinite(n) && n > 0 ? n : null };
  };

  /* Why a project cannot take offers ('' = it can). Never invents a value. */
  const availability = (enabledRaw, publishedRaw, minRaw) => {
    if (String(enabledRaw).toLowerCase() === 'false' || String(minRaw || '').trim().toLowerCase() === 'off') return { reason: 'disabled' };
    const pub = parsePublished(publishedRaw);
    if (!String(minRaw || '').trim()) return { reason: 'no_min', pub };
    const min = parseMinimum(minRaw, pub.currency).amount;
    if (!min || (pub.amount && min > pub.amount)) return { reason: 'bad_min', pub };
    return { reason: '', pub, min };
  };

  /* 'below' | 'above' | '' for an already-parsed positive amount. */
  const checkRange = (amount, min, max) => (amount < min ? 'below' : (amount > max ? 'above' : ''));

  /* Same grouping convention as the existing price on the page: "17 900 MAD". */
  const fmtAmount = (n, currency) => {
    const hasFraction = Math.abs(n - Math.round(n)) > 1e-9;
    const txt = n.toLocaleString('fr-FR', { minimumFractionDigits: hasFraction ? 2 : 0, maximumFractionDigits: 2 }).replace(/[  ]/g, ' ');
    return currency ? `${txt} ${currency}` : txt;
  };

  /* Visitor input -> {ok, amount} | {ok:false, error}. error is one of required|invalid|positive|max. */
  const parseOffer = input => {
    let s = normaliseDigits(input).replace(/[\s  ]/g, '');
    if (!s) return { ok: false, error: 'required' };
    if (/^[-−]/.test(s)) return /^[-−]\d[\d.,]*$/.test(s) ? { ok: false, error: 'positive' } : { ok: false, error: 'invalid' };
    let value;
    if (/^\d{1,3}([.,]\d{3})+$/.test(s)) value = Number(s.replace(/[.,]/g, ''));     // 17,900 · 17.900 · 1,250,000
    else if (/^\d+([.,]\d{1,2})?$/.test(s)) value = Number(s.replace(',', '.'));      // 17900 · 17900.50 · 17900,5
    else return { ok: false, error: 'invalid' };
    if (!Number.isFinite(value)) return { ok: false, error: 'invalid' };
    if (value <= 0) return { ok: false, error: 'positive' };
    if (value > MAX_AMOUNT) return { ok: false, error: 'max' };
    return { ok: true, amount: Math.round(value * 100) / 100 };
  };

  const isRtl = () => (document.documentElement.dir || '').toLowerCase() === 'rtl' || /^ar/i.test(document.documentElement.lang || '');
  const ltr = s => (isRtl() ? '\u2066' + s + '\u2069' : s);   // LRI … PDI keeps amount + currency in reading order inside Arabic text

  const cleanText = (s, max) => String(s || '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f‎‏‪-‮⁦-⁩]/g, '').replace(/[ \t]+/g, ' ').trim().slice(0, max);

  const validEmail = s => s.length <= 254 && /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/.test(s);
  const waDigits = s => {
    const t = normaliseDigits(s).trim();
    if (!/^\+?[\d\s().-]+$/.test(t)) return '';
    let d = t.replace(/\D/g, '');
    if (d.startsWith('00')) d = d.slice(2);
    return d.length >= 8 && d.length <= 15 ? d : '';
  };

  const pctDiff = (offer, original) => {
    if (!original) return null;
    const p = Math.round(Math.abs(offer - original) / original * 1000) / 10;
    return p;
  };
  const fmtPct = p => ((document.documentElement.lang || '').toLowerCase().startsWith('fr') ? String(p).replace('.', ',') : String(p));

  /* Plain-text WhatsApp message. Values are only ever inserted as text and URL-encoded by the caller. */
  const buildMessage = o => {
    const L = [
      T('offer_wa_hello', 'Hello TEYRA STUDIO,'), '',
      T('offer_wa_intro', 'I would like to make an offer on the following ready-made website.'), '',
      `${T('offer_wa_project', 'Project')}: ${o.title}`,
      o.slug ? `${T('offer_wa_ref', 'Project reference')}: ${o.slug}` : '',
      `${T('offer_wa_original', 'Original Price')}: ${o.originalLabel}`,
      o.minLabel ? `${T('offer_wa_minimum', 'Minimum Offer Price')}: ${o.minLabel}` : '',
      `${T('offer_wa_proposed', 'My Proposed Price')}: ${o.offerLabel}`, '',
      `${T('offer_wa_name', 'Name')}: ${o.name}`,
      `${T('offer_wa_contact', 'Preferred Contact')}: ${o.method === 'email' ? T('offer_email', 'Email') : T('offer_wa', 'WhatsApp')}`,
      o.method === 'email' ? `${T('offer_wa_email', 'Email')}: ${o.email}` : `${T('offer_wa_phone', 'WhatsApp')}: ${o.phone}`
    ];
    if (o.message) L.push('', `${T('offer_wa_message', 'Additional Message')}:`, o.message);
    L.push('', T('offer_wa_disclaimer', 'I understand that this is a proposal subject to review and confirmation by TEYRA STUDIO.'), '', T('offer_wa_closing', 'Please contact me to discuss the offer.'), '', T('offer_wa_thanks', 'Thank you.'));
    return L.filter(l => l !== undefined && l !== null).join('\n').replace(/\n{3,}/g, '\n\n');
  };
  const buildWaUrl = (number, text) => `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

  window.teyraOffer = { offerConfig, parsePublished, parseMinimum, availability, checkRange, fmtAmount, parseOffer, validEmail, waDigits, pctDiff, buildMessage, buildWaUrl, cleanText };

  /* ---------- dialog ---------- */

  const esc = v => String(v == null ? '' : v).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  let root = null, opener = null, ctx = null, busy = false, uid = 0;

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const build = () => {
    uid += 1;
    const id = 'offer' + uid;
    const el = document.createElement('div');
    el.className = 'offer-modal';
    el.hidden = true;
    el.innerHTML = `
      <div class="offer-backdrop" data-offer-close></div>
      <div class="offer-panel" role="dialog" aria-modal="true" aria-labelledby="${id}-title" aria-describedby="${id}-note" tabindex="-1">
        <button class="offer-x" type="button" data-offer-close aria-label="${esc(T('offer_close', 'Close'))}"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
        <div class="offer-view" data-offer-form-view>
          <header class="offer-head">
            <p class="offer-eyebrow">TEYRA STUDIO</p>
            <h2 id="${id}-title">${esc(T('offer_title', 'Make an Offer'))}</h2>
            <p class="offer-sub">${esc(T('offer_sub', 'Propose your price for this ready-made website.'))}</p>
          </header>
          <form class="offer-form" novalidate data-offer-form>
            <dl class="offer-summary">
              <div><dt>${esc(T('offer_project', 'Project'))}</dt><dd data-offer-project></dd></div>
              <div><dt>${esc(T('offer_original', 'Original Price'))}</dt><dd data-offer-original></dd></div>
              <div class="offer-summary-min"><dt>${esc(T('offer_minimum', 'Minimum Offer'))}</dt><dd data-offer-minimum></dd></div>
            </dl>
            <div class="offer-field" data-field="price">
              <label for="${id}-price">${esc(T('offer_price', 'Your Proposed Price'))} <abbr title="*" aria-hidden="true">*</abbr></label>
              <div class="offer-price-row"><input id="${id}-price" name="price" type="text" inputmode="decimal" autocomplete="off" required aria-required="true" placeholder="${esc(T('offer_price_ph', 'e.g. 15 000'))}" aria-describedby="${id}-price-hint ${id}-price-err"><span class="offer-currency" data-offer-currency></span></div>
              <p class="offer-hint offer-range" data-offer-range></p>
              <p class="offer-hint" id="${id}-price-hint" aria-live="polite" data-offer-hint></p>
              <p class="offer-error" id="${id}-price-err" data-error hidden></p>
            </div>
            <div class="offer-field" data-field="name">
              <label for="${id}-name">${esc(T('offer_name', 'Full Name'))} <abbr title="*" aria-hidden="true">*</abbr></label>
              <input id="${id}-name" name="name" type="text" autocomplete="name" maxlength="80" required aria-required="true" placeholder="${esc(T('offer_name_ph', 'Your full name'))}" aria-describedby="${id}-name-err">
              <p class="offer-error" id="${id}-name-err" data-error hidden></p>
            </div>
            <fieldset class="offer-field offer-methods" data-field="method">
              <legend>${esc(T('offer_contact', 'Preferred Contact Method'))}</legend>
              <label class="offer-radio"><input type="radio" name="method" value="whatsapp" checked><span>${esc(T('offer_wa', 'WhatsApp'))}</span></label>
              <label class="offer-radio"><input type="radio" name="method" value="email"><span>${esc(T('offer_email', 'Email'))}</span></label>
            </fieldset>
            <div class="offer-field" data-field="phone">
              <label for="${id}-phone">${esc(T('offer_wa_num', 'WhatsApp Number'))} <abbr title="*" aria-hidden="true">*</abbr></label>
              <input id="${id}-phone" name="phone" type="tel" inputmode="tel" autocomplete="tel" maxlength="30" dir="ltr" required aria-required="true" placeholder="${esc(T('offer_wa_num_ph', '+212 6 00 00 00 00'))}" aria-describedby="${id}-phone-err">
              <p class="offer-error" id="${id}-phone-err" data-error hidden></p>
            </div>
            <div class="offer-field" data-field="email" hidden>
              <label for="${id}-email">${esc(T('offer_email_addr', 'Email Address'))} <abbr title="*" aria-hidden="true">*</abbr></label>
              <input id="${id}-email" name="email" type="email" inputmode="email" autocomplete="email" maxlength="254" dir="ltr" required aria-required="true" placeholder="${esc(T('offer_email_ph', 'you@business.com'))}" aria-describedby="${id}-email-err">
              <p class="offer-error" id="${id}-email-err" data-error hidden></p>
            </div>
            <div class="offer-field" data-field="message">
              <label for="${id}-message">${esc(T('offer_message', 'Message (optional)'))}</label>
              <textarea id="${id}-message" name="message" rows="3" maxlength="${MAX_MESSAGE}" placeholder="${esc(T('offer_message_ph', 'Project requirements or negotiation details'))}"></textarea>
            </div>
            <p class="offer-note" id="${id}-note">${esc(T('offer_note', 'Your offer is a proposal only. It does not change the published price or constitute an accepted purchase. TEYRA STUDIO will review your offer and contact you.'))}</p>
            <p class="offer-form-error" data-offer-form-error role="alert" hidden></p>
            <button class="button button-dark offer-submit" type="submit" data-offer-submit>${esc(T('offer_submit', 'Submit My Offer'))}</button>
          </form>
        </div>
        <div class="offer-view offer-unavailable" data-offer-unavailable hidden>
          <header class="offer-head">
            <p class="offer-eyebrow">TEYRA STUDIO</p>
            <h2 tabindex="-1" id="${id}-un-title" data-offer-unavailable-title>${esc(T('offer_title', 'Make an Offer'))}</h2>
            <p class="offer-sub" data-offer-project-un></p>
          </header>
          <p class="offer-note" data-offer-unavailable-text></p>
          <p class="offer-form-error" data-offer-config-note hidden></p>
          <div class="offer-done-actions">
            <a class="button button-dark" data-offer-buy>${esc(T('offer_unavail_buy', 'Buy This Website'))}</a>
            <button class="button button-outline" type="button" data-offer-close>${esc(T('offer_close', 'Close'))}</button>
          </div>
        </div>
        <div class="offer-view offer-done" data-offer-done hidden>
          <span class="offer-done-ico" aria-hidden="true"><svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span>
          <h2 tabindex="-1" data-offer-done-title>${esc(T('offer_ok_title', 'Your offer is ready to send'))}</h2>
          <p>${esc(T('offer_ok_text', 'WhatsApp is opening with your offer prepared. Press Send in WhatsApp to deliver it — TEYRA STUDIO only receives your offer once you send the message.'))}</p>
          <p class="offer-note">${esc(T('offer_note', ''))}</p>
          <div class="offer-done-actions">
            <a class="button button-dark" data-offer-again target="_blank" rel="noopener noreferrer">${esc(T('offer_ok_again', 'Open WhatsApp again'))}</a>
            <button class="button button-outline" type="button" data-offer-close>${esc(T('offer_close', 'Close'))}</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(el);
    return el;
  };

  const $ = (sel, scope) => (scope || root).querySelector(sel);
  const form = () => $('[data-offer-form]');
  const field = name => $(`[data-field="${name}"]`);
  const input = name => form().elements[name];

  const setError = (name, msg) => {
    const f = field(name); if (!f) return;
    const err = $('[data-error]', f);
    const ctl = name === 'method' ? null : input(name);
    if (err) { err.textContent = msg || ''; err.hidden = !msg; }
    if (ctl) { if (msg) ctl.setAttribute('aria-invalid', 'true'); else ctl.removeAttribute('aria-invalid'); }
    f.classList.toggle('has-error', !!msg);
  };
  const method = () => (form().elements.method.value === 'email' ? 'email' : 'whatsapp');

  const validators = {
    price() {
      const r = parseOffer(input('price').value);
      if (r.ok && ctx && ctx.available) {
        const side = (r.amount < ctx.min ? 'below' : (ctx.original && r.amount > ctx.original ? 'above' : ''));
        if (side === 'below') return T('offer_err_price_below', 'The minimum offer for this project is {minimum}. Please enter an amount equal to or above this minimum.', { minimum: ctx.minLabel });
        if (side === 'above') return T('offer_err_price_above', 'Your offer cannot exceed the published price. You can purchase the website at its listed price.');
      }
      return r.ok ? '' : T('offer_err_price_' + r.error, { required: 'Please enter your proposed price.', invalid: 'Enter a valid amount using numbers only, for example 15000.', positive: 'Your proposed price must be greater than zero.', max: 'That amount is too large. Please check it.' }[r.error]);
    },
    name() { return cleanText(input('name').value, 80).length >= 2 ? '' : T('offer_err_name', 'Please enter your full name.'); },
    phone() {
      if (method() !== 'whatsapp') return '';
      const v = input('phone').value.trim();
      if (!v) return T('offer_err_wa_required', 'Please enter your WhatsApp number.');
      return waDigits(v) ? '' : T('offer_err_wa_invalid', 'Enter a valid number with its country code, for example +212 6 00 00 00 00.');
    },
    email() {
      if (method() !== 'email') return '';
      const v = input('email').value.trim();
      if (!v) return T('offer_err_email_required', 'Please enter your email address.');
      return validEmail(v) ? '' : T('offer_err_email_invalid', 'Please enter a valid email address.');
    }
  };
  const validateField = name => { const msg = validators[name](); setError(name, msg); return msg; };

  const updateHint = () => {
    const hint = $('[data-offer-hint]');
    const r = parseOffer(input('price').value);
    if (!r.ok) { hint.textContent = ''; return; }
    if (checkRange(r.amount, ctx.min, ctx.original)) { hint.textContent = ''; return; }
    const amount = ltr(fmtAmount(r.amount, ctx.currency));
    if (!ctx.original) { hint.textContent = T('offer_diff_none', 'You are offering {amount}.', { amount }); return; }
    const p = pctDiff(r.amount, ctx.original);
    const key = r.amount === ctx.original ? 'same' : (r.amount < ctx.original ? 'below' : 'above');
    const dflt = { same: 'You are offering {amount} — the same as the published price (for reference only).', below: 'You are offering {amount} — {pct}% below the published price (for reference only).', above: 'You are offering {amount} — {pct}% above the published price (for reference only).' }[key];
    hint.textContent = T('offer_diff_' + key, dflt, { amount, pct: fmtPct(p) });
  };

  const syncMethod = () => {
    const wa = method() === 'whatsapp';
    field('phone').hidden = !wa; field('email').hidden = wa;
    input('phone').disabled = !wa; input('email').disabled = wa;
    setError('phone', ''); setError('email', '');
  };

  const setBusy = on => {
    busy = on;
    const btn = $('[data-offer-submit]');
    btn.disabled = on;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.textContent = on ? T('offer_preparing', 'Preparing your offer…') : T('offer_submit', 'Submit My Offer');
  };
  const showFormError = msg => { const p = $('[data-offer-form-error]'); p.textContent = msg || ''; p.hidden = !msg; };

  const reset = () => {
    form().reset();
    setBusy(false); showFormError('');
    ['price', 'name', 'phone', 'email'].forEach(n => setError(n, ''));
    $('[data-offer-hint]').textContent = '';
    $('[data-offer-form-view]').hidden = false; $('[data-offer-done]').hidden = true;
    syncMethod();
    $('[data-offer-project]').textContent = ctx.title;
    $('[data-offer-original]').textContent = ctx.originalLabel;
    $('[data-offer-currency]').textContent = ctx.currency;
    // Minimum + range hint exist only for a project whose owner configured a valid minimum.
    const minRow = $('.offer-summary-min'), range = $('[data-offer-range]');
    minRow.hidden = true; range.hidden = !ctx.available;
    $('[data-offer-minimum]').textContent = ctx.available ? ctx.minLabel : '';
    range.textContent = ctx.available ? T('offer_range', 'Your proposal will be reviewed by TEYRA STUDIO. You can also purchase this website at its listed price.') : '';
    // Unavailable project: no form can be submitted. Visitors see a neutral message; the owner (theme editor) sees why.
    const un = $('[data-offer-unavailable]');
    $('[data-offer-form-view]').hidden = !ctx.available;
    un.hidden = ctx.available;
    if (!ctx.available) {
      $('[data-offer-project-un]').textContent = ctx.title;
      $('[data-offer-unavailable-text]').textContent = T('offer_unavail_text', 'Offers are not available for this project at the moment. You can purchase the website at its listed price, or contact TEYRA STUDIO to discuss your needs.');
      const cfg = $('[data-offer-config-note]');
      const owner = !!(window.TEYRA_CFG && window.TEYRA_CFG.designMode);
      cfg.textContent = owner ? T('offer_cfg_' + ctx.reason, {
        disabled: 'Owner note: offers are switched off for this project in the theme settings.',
        no_price: 'Owner note: this project has no published price, so offers cannot be validated.',
        no_min: 'Owner note: no minimum offer price is configured for this project. Set “Minimum offer price” on the project block.',
        bad_min: 'Owner note: the minimum offer price must be a number (in the project’s currency) that is greater than zero and not above the published price.'
      }[ctx.reason]) : '';
      cfg.hidden = !owner;
      const buy = $('[data-offer-buy]');
      buy.href = `${window.teyraRoot ? window.teyraRoot() : '/'}pages/start-project?project=${encodeURIComponent(ctx.slug)}&project_title=${encodeURIComponent(ctx.title)}&category=${encodeURIComponent(ctx.category)}&request_type=purchase${ctx.original ? '&project_price=' + encodeURIComponent(ctx.priceRaw) : ''}#contact`;
    }
  };

  const sizeToViewport = () => {
    // Keeps the panel (and its submit button) inside the visible area when a mobile keyboard is open.
    if (!root || root.hidden) return;
    const vv = window.visualViewport;
    root.style.setProperty('--offer-vh', (vv ? vv.height : window.innerHeight) + 'px');
    root.style.setProperty('--offer-top', (vv ? vv.offsetTop : 0) + 'px');
  };

  const open = trigger => {
    if (!root) root = build();
    const d = trigger.dataset;
    const av = availability(d.offerEnabled, d.offerPrice, d.offerMin);
    const pub = av.pub || parsePublished(d.offerPrice);
    ctx = {
      slug: cleanText(d.offerSlug, 120),
      title: cleanText(d.offerTitle, 160) || 'TEYRA STUDIO',
      category: cleanText(d.offerCategory, 120),
      priceRaw: cleanText(d.offerPrice, 60),
      original: pub.amount,
      currency: pub.currency,
      originalLabel: pub.amount ? ltr(fmtAmount(pub.amount, pub.currency)) : T('price_request', 'Price available on request'),
      available: !av.reason,
      reason: av.reason,
      min: av.min || null,
      minLabel: av.min ? ltr(fmtAmount(av.min, pub.currency)) : ''
    };
    opener = trigger;
    reset();
    root.hidden = false;
    document.documentElement.classList.add('offer-lock');
    sizeToViewport();
    requestAnimationFrame(() => { root.classList.add('is-open'); const p = $('.offer-panel'); p.scrollTop = 0; (ctx.available ? input('price') : $('[data-offer-unavailable-title]')).focus({ preventScroll: true }); });
  };

  const close = () => {
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.hidden = true;
    document.documentElement.classList.remove('offer-lock');
    setBusy(false);
    if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    opener = null;
  };

  const openWhatsApp = url => {
    // A real <a target=_blank rel=noopener> click: works inside the user gesture and does not navigate the current page.
    const a = document.createElement('a');
    a.href = url; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.style.display = 'none';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const submit = e => {
    e.preventDefault();
    if (busy || !ctx || !ctx.available) return;
    const names = ['price', 'name', 'phone', 'email'];
    const bad = names.filter(n => validateField(n));
    if (bad.length) {
      showFormError(T('offer_err_form', 'Please correct the highlighted fields.'));
      input(bad[0]).focus();
      return;
    }
    showFormError('');
    setBusy(true);
    try {
      const number = String((window.TEYRA_CFG || {}).whatsapp || '').replace(/\D/g, '');
      if (number.length < 8 || number.length > 15) {
        showFormError(T('offer_err_nowa', 'The TEYRA STUDIO WhatsApp number has not been configured yet, so your offer cannot be prepared. Please contact us through the contact page instead.'));
        setBusy(false); return;
      }
      const offer = parseOffer(input('price').value);
      const m = method();
      const text = buildMessage({
        title: ctx.title, slug: ctx.slug, originalLabel: ctx.originalLabel, minLabel: ctx.minLabel,
        offerLabel: ltr(fmtAmount(offer.amount, ctx.currency)),
        name: cleanText(input('name').value, 80), method: m,
        phone: m === 'whatsapp' ? '+' + waDigits(input('phone').value) : '',
        email: m === 'email' ? cleanText(input('email').value, 254) : '',
        message: cleanText(input('message').value, MAX_MESSAGE)
      });
      const url = buildWaUrl(number, text);
      openWhatsApp(url);
      $('[data-offer-again]').href = url;
      $('[data-offer-form-view]').hidden = true;
      const done = $('[data-offer-done]'); done.hidden = false;
      $('[data-offer-done-title]').focus({ preventScroll: true });
      $('.offer-panel').scrollTop = 0;
    } catch (err) {
      showFormError(T('offer_err_generic', 'Something went wrong while preparing your offer. Please try again.'));
      setBusy(false);
    }
  };

  /* ---------- wiring ---------- */

  document.addEventListener('click', e => {
    const trigger = e.target.closest && e.target.closest('[data-offer-open]');
    if (trigger) { e.preventDefault(); open(trigger); return; }
    if (root && !root.hidden && e.target.closest('[data-offer-close]') && root.contains(e.target)) close();
  });

  document.addEventListener('keydown', e => {
    if (!root || root.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const items = Array.from($('.offer-panel').querySelectorAll(FOCUSABLE)).filter(n => !n.closest('[hidden]') && n.offsetParent !== null);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (!$('.offer-panel').contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('submit', e => { if (root && e.target === form()) submit(e); });
  document.addEventListener('change', e => { if (root && e.target.name === 'method' && root.contains(e.target)) syncMethod(); });
  document.addEventListener('input', e => {
    if (!root || root.hidden || !root.contains(e.target)) return;
    const n = e.target.name;
    if (n === 'price') { updateHint(); if (field('price').classList.contains('has-error')) validateField('price'); }
    else if (validators[n] && field(n).classList.contains('has-error')) validateField(n);
  });
  document.addEventListener('focusout', e => {
    if (!root || root.hidden || !root.contains(e.target)) return;
    const n = e.target.name;
    if (validators[n] && e.target.value) validateField(n);
  });
  document.addEventListener('focusin', e => {
    if (!root || root.hidden || !root.contains(e.target) || !/^(INPUT|TEXTAREA)$/.test(e.target.tagName)) return;
    setTimeout(() => { try { e.target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {} }, 250);
  });
  if (window.visualViewport) { window.visualViewport.addEventListener('resize', sizeToViewport); window.visualViewport.addEventListener('scroll', sizeToViewport); }
  window.addEventListener('resize', sizeToViewport);
})();
